import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core'
import LensIcon from '@material-ui/icons/Lens'
import ErrorIcon from '@material-ui/icons/Error'
import type { SvgIconProps } from '@material-ui/core/SvgIcon'
import type { Activity, NoteActivity, PaymentActivityDetails } from '@paintscout/api'
import type { StyleClasses } from '../../styles'
import { RoundedBoltIcon } from '../../icons'

export interface ActivityDotProps extends Omit<SvgIconProps, 'color'> {
  classes?: StyleClasses<typeof useStyles>
  activity: Activity
  color?: string
}

const useStyles = makeStyles<Theme, ActivityDotProps>(
  (theme) => {
    return {
      root: ({ activity, ...props }) => {
        let color = props.color ?? theme.palette.quote.status.draft
        const quote = activity.details?.quote || activity.details?.invoice

        switch (activity.details.activityType) {
          case 'status-changed':
          case 'status-updated':
          case 'restored':
          case 'thank-you-sent':
          case 'declined-sent':
          case 'follow-up-sent':
            color = theme.palette.quote.status[quote.status]
            break
          default:
            color = theme.palette.activityType[activity.details.activityType] ?? '#AAAAAA'
            break
        }

        if (activity.details.activityType === 'payment') {
          const { invoice } = activity.details as PaymentActivityDetails
          const final =
            invoice?.status === 'paid' || (activity.details?.events ?? []).some((event) => event === 'fully paid')

          if ((activity.details as PaymentActivityDetails).isProcessing) {
            color = theme.palette.quote.status.draft
          } else if ((activity.details as PaymentActivityDetails).isFailed) {
            color = theme.palette.quote.status.declined
          } else if (final) {
            color = theme.palette.quote.status.paid
          }
        }

        if (activity.details.activityType === 'payment-received-sent') {
          const fullyPaid = (activity.details?.events ?? []).some((event) => event === 'fully paid')
          color = fullyPaid ? theme.palette.quote.status.paid : theme.palette.quote.status.partial
        }

        return {
          color,
          position: 'relative',
          zIndex: 2,
          marginTop: 5,
          marginRight: theme.spacing(0.75),
          width: 11,
          height: 11
        }
      }
    }
  },
  {
    name: 'ActivityDot'
  }
)

const ActivityDot = React.forwardRef<SVGElement, ActivityDotProps>(function ActivityDot(props, ref) {
  const { activity, color: _color, ...svgProps } = props
  const classes = useStyles(props)
  const theme = useTheme()
  const error = activity.details.quote?.error || activity.details.invoice?.error
  const unownedNote =
    (activity as NoteActivity).details.activityType === 'note' && (activity as NoteActivity)?.details?.owner === null

  if (unownedNote) {
    return <RoundedBoltIcon {...svgProps} classes={classes} style={{ marginTop: theme.spacing(0.25) }} />
  }
  const IconComponent = error ? ErrorIcon : LensIcon

  return <IconComponent {...svgProps} classes={classes} ref={ref as any} />
})

export default ActivityDot
